import { useAtom } from 'jotai/index';
import {
  deletingConfigurationLoadingAtom,
  gettingConfigurationsLoadingAtom,
  configurationsAtom,
  configurationsPagesInfoAtom,
  editingConfigurationLoadingAtom,
  configurationsProjectAtom,
  configurationsItemsOnPageAtom,
  downloadingFileLoadingAtom,
} from '../atoms/configurationsAtoms';
import sendRequest from '../helpers/sendRequest';
import Api from '../api';
import {
  closeModalAtom, modalStateAtom, openConfirmModalAtom, openInfoModalAtom, openLoadingModalAtom,
} from '../atoms/modalAtoms';
import useTranslations from './useTranslations';
import { DICTIONARY } from '../translations/dictionary';
import AddToProjectModal from '../pages/Configurations/components/AddToProjectModal';
import { configuratorSingleConfigurationAtom } from '../atoms/configuratorAtoms';
import { api } from '../api/configs';
import FilesServices from '../services/FilesServices';
import { isAdminAtom } from '../atoms/accountAtoms';
import useTutorials from './useTutorials';

const useConfigurations = ({ setSelectedItems } = {}) => {
  const { t } = useTranslations();
  const { changeTutorialStep, tutorialStep } = useTutorials();

  const [isAdmin] = useAtom(isAdminAtom);
  const [configurations, setConfigurations] = useAtom(configurationsAtom);
  const [gettingConfigurationsLoading, setGettingConfigurationsLoading] = useAtom(gettingConfigurationsLoadingAtom);
  const [editingConfigurationLoading, setEditingConfigurationLoading] = useAtom(editingConfigurationLoadingAtom);
  const [deletingConfigurationLoading, setDeletingConfigurationLoading] = useAtom(deletingConfigurationLoadingAtom);
  const [downloadingFileLoading, setDownloadingFileLoading] = useAtom(downloadingFileLoadingAtom);
  const [configurationsPagesInfo, setConfigurationsPagesInfo] = useAtom(configurationsPagesInfoAtom);
  const [configurationsProject, setConfigurationsProject] = useAtom(configurationsProjectAtom);
  const [configurationsItemsOnPage] = useAtom(configurationsItemsOnPageAtom);
  const [configuratorSingleConfiguration, setConfiguratorSingleConfiguration] = useAtom(configuratorSingleConfigurationAtom);

  const [, setModalState] = useAtom(modalStateAtom);
  const [, openConfirmModal] = useAtom(openConfirmModalAtom);
  const [, openInfoModal] = useAtom(openInfoModalAtom);
  const [, openLoadingModal] = useAtom(openLoadingModalAtom);
  const [, closeModal] = useAtom(closeModalAtom);

  const getConfigurations = async ({ page, shouldAddToExisting }) => {
    const data = await sendRequest({
      request: Api.getConfigurations,
      payload: { page, items_on_page: configurationsItemsOnPage },
      setLoading: setGettingConfigurationsLoading,
    });

    if (data?.status !== 'ok') return;

    if (page !== 1 && page !== '1' && shouldAddToExisting) setConfigurations((prev) => [...prev, ...data.data.configurations]);
    else setConfigurations(data.data.configurations);

    setConfigurationsPagesInfo(data.data.pages);
  };

  const getConfigurationsOfProject = async ({
    page, projectId, shouldAddToExisting,
  }) => {
    const data = await sendRequest({
      request: Api.getConfigurationsOfProject,
      payload: { id: projectId, params: { page, items_on_page: configurationsItemsOnPage } },
      setLoading: shouldAddToExisting ? undefined : setGettingConfigurationsLoading,
    });

    if (data?.status !== 'ok') return;

    const { project } = data.data;
    if (page !== 1 && page !== '1' && shouldAddToExisting) setConfigurations((prev) => [...prev, ...project.configurations.list]);
    else setConfigurations(project.configurations.list);

    setConfigurationsPagesInfo(project.configurations.pages);
    setConfigurationsProject({ ...project, configurations: undefined });
  };

  const editConfiguration = (configuration, cb) => async ({ project, name, description }) => {
    if (editingConfigurationLoading) return;

    const body = { id: configuration.id };
    if (project?.id) body.project_id = project.id;
    if (name) body.name = name;
    if (description) body.description = description;

    const data = await sendRequest({
      request: Api.editConfiguration,
      payload: body,
      setLoading: setEditingConfigurationLoading,
    });

    if (data?.status === 'ok') {
      const { configuration: newConfiguration } = data.data;
      cb?.(newConfiguration);
      setConfigurations((prev) => prev.map((i) => (i.id === newConfiguration.id ? ({ ...i, ...newConfiguration }) : i)));
      closeModal();
    }
  };

  const detachConfigurations = async (projectId, listOfIds) => {
    if (deletingConfigurationLoading) return;

    const data = await sendRequest({
      request: Api.detachConfigurations,
      payload: { id: projectId, configurations: listOfIds },
      setLoading: setDeletingConfigurationLoading,
    });

    if (data?.status === 'ok') {
      setSelectedItems([]);
      setConfigurations((prev) => prev.map((i) => (listOfIds.includes(i.id) ? { ...i, project_id: 0 } : i)));
      setConfigurationsPagesInfo((prev) => ({ ...prev, total_records: prev.total_records - listOfIds.length }));
      setConfigurations((prev) => prev.filter((i) => !listOfIds.includes(i.id)));
      closeModal();
    }
  };

  const deleteConfigurations = async (listOfIds, cb) => {
    if (deletingConfigurationLoading) return;

    const data = await sendRequest({
      request: Api.deleteConfigurations,
      payload: { ids: listOfIds },
      setLoading: setDeletingConfigurationLoading,
    });

    if (data?.status === 'ok') {
      setSelectedItems([]);
      setConfigurationsPagesInfo((prev) => ({ ...prev, total_records: prev.total_records - listOfIds.length }));
      setConfigurations((prev) => prev.filter((i) => !listOfIds.includes(i.id)));
      cb?.();
      closeModal();
    }
  };

  /* Files Start */
  const downloadConfigurationsArchive = async (ids) => {
    if (downloadingFileLoading) return;

    if (tutorialStep === 9) {
      return changeTutorialStep();
    }

    openLoadingModal();
    const body = { ids };

    const response = await sendRequest({
      request: Api.generateConfigurationsArchive,
      payload: body,
      setLoading: setDownloadingFileLoading,
      getFullResponse: true,
    });

    const { data, headers } = response || {};

    let isError = false;
    if (data?.status !== 'failed') {
      const downloadRes = FilesServices.downloadPdf(data, headers);
      isError = downloadRes.isError;
    }

    if (!isError) closeModal();
  };

  const downloadConfigurationsPdf = async (ids) => {
    if (downloadingFileLoading) return;

    openLoadingModal();
    const body = { ids };

    const response = await sendRequest({
      request: Api.generateConfigurationsPdf,
      payload: body,
      setLoading: setDownloadingFileLoading,
      getFullResponse: true,
    });

    const { data, headers } = response || {};

    let isError = false;
    if (data?.status !== 'failed') {
      const downloadRes = FilesServices.downloadPdf(data, headers);
      isError = downloadRes.isError;
    }

    if (!isError) closeModal();
  };

  const downloadConfigurationsDocx = async (ids) => {
    if (downloadingFileLoading) return;

    openLoadingModal();
    const body = { ids };

    const response = await sendRequest({
      request: Api.generateConfigurationsDocx,
      payload: body,
      setLoading: setDownloadingFileLoading,
      getFullResponse: true,
    });

    const { data, headers } = response || {};

    let isError = false;
    if (data?.status !== 'failed') {
      const downloadRes = FilesServices.downloadDocx(data, headers);
      isError = downloadRes.isError;
    }

    if (!isError) closeModal();
  };

  const downloadConfigurationsFile = async (ids) => {
    if (isAdmin && false) await downloadConfigurationsDocx(ids);
    else await downloadConfigurationsPdf(ids);
  };

  const emailConfigurationsArchive = async (ids) => {
    if (downloadingFileLoading) return;

    if (tutorialStep === 10) {
      return changeTutorialStep();
    }

    openLoadingModal();
    const body = { ids, return_to: 'mail' };

    const data = await sendRequest({
      request: Api.generateConfigurationsArchive,
      payload: body,
      setLoading: setDownloadingFileLoading,
    });

    if (data?.status !== 'failed') {
      openInfoModal({ text: data.message || t(DICTIONARY.emailSuccess) });
    } else {
      closeModal();
    }
  };
  /* Files End */

  const handleConfigurationDescription = async (val) => {
    if (!configuratorSingleConfiguration?.id) return;
    await editConfiguration(configuratorSingleConfiguration, (newConfiguration) => {
      const newObj = { name: newConfiguration.name, project_id: newConfiguration.project_id, description: newConfiguration.description };
      setConfiguratorSingleConfiguration((prev) => ({ ...prev, ...newObj }));
    })({ description: val });
  };

  const onAddToProjectClick = (configuration, cb) => {
    if (tutorialStep === 11) {
      changeTutorialStep();
    }

    setModalState({
      isOpen: true,
      withCloseButton: true,
      children: <AddToProjectModal configuration={configuration} onSave={editConfiguration(configuration, cb)} />,
    });
  };

  const onDetachConfigurationsClick = (projectId, listOfIds) => {
    openConfirmModal({
      text: listOfIds.length === 1 ? t(DICTIONARY.sureToDetachConfiguration) : t(DICTIONARY.sureToDetachConfigurations, listOfIds.length),
      onButtonClick: () => detachConfigurations(projectId, listOfIds),
    });
  };

  const onDeleteConfigurationsClick = (listOfIds, cb) => {
    openConfirmModal({
      text: listOfIds.length === 1 ? t(DICTIONARY.sureToDeleteConfiguration) : t(DICTIONARY.sureToDeleteConfigurations, listOfIds.length),
      onButtonClick: () => deleteConfigurations(listOfIds, cb),
    });
  };

  return {
    configurations,
    gettingConfigurationsLoading,
    editingConfigurationLoading,
    configurationsPagesInfo,
    configurationsProject,
    getConfigurations,
    getConfigurationsOfProject,
    onAddToProjectClick,
    onDeleteConfigurationsClick,
    onDetachConfigurationsClick,
    handleConfigurationDescription,
    downloadConfigurationsArchive,
    downloadConfigurationsFile,
    emailConfigurationsArchive,
  };
};

export default useConfigurations;
